"use client";

import { c as _c } from "react/compiler-runtime";
import { getCatalogPromotionPresentation } from "../promotion-messaging";
import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiImage } from "react-icons/fi";
import Image from "next/image";
import { PopularBadge } from "@/components/ui/popular-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useShopDisplaySettings } from "@/contexts/shop-display-settings-provider";
import { isShopReturnTarget } from "@/hooks/shop/use-shop-scroll-restore";
import { formatCurrency } from "@/lib/format-data";
import { canOptimizeImage, resolveImageUrl } from "@/lib/image-utils";
import { cx } from "@/lib/tailwind-utils";
import { CatalogTileProps, priceForShopItem, savingsForShopItem } from "./catalog-card-primitives";
import { preloadShopItemPagePhoto } from "./shop-item-gallery";
import { ShopPhotoTransition } from "./shop-photo-transition";
const HOVER_PRELOAD_DELAY_MS = 150;

/** A retail-style product tile for Storefront shops: the whole photo, then name and price. */
export const StorefrontProductTile: React.FC<CatalogTileProps> = t0 => {
  const $ = _c(53);
  const {
    item,
    onSelect,
    className,
    opensDialog: t1,
    placement,
    quantity
  } = t0;
  const opensDialog = t1 === undefined ? false : t1;
  const interactive = Boolean(onSelect);
  const {
    accentColor,
    photoTransitions
  } = useShopDisplaySettings();
  const canPreload = photoTransitions && interactive;
  const hoverTimer = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (hoverTimer.current !== null) {
        window.clearTimeout(hoverTimer.current);
      }
      hoverTimer.current = null;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const cancelHoverPreload = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => cancelHoverPreload;
    t4 = [];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== canPreload || $[4] !== item) {
    t5 = () => {
      if (canPreload) {
        preloadShopItemPagePhoto(item);
      }
    };
    $[3] = canPreload;
    $[4] = item;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const preloadPagePhoto = t5;
  const Root = interactive ? "button" : "div";
  let t6;
  if ($[6] !== interactive || $[7] !== onSelect || $[8] !== preloadPagePhoto) {
    t6 = interactive ? {
      type: "button" as const,
      onClick: () => {
        preloadPagePhoto();
        onSelect?.();
      }
    } : {};
    $[6] = interactive;
    $[7] = onSelect;
    $[8] = preloadPagePhoto;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const rootProps = t6;
  const currentPrice = priceForShopItem(item);
  const savings = savingsForShopItem(item);
  let t7;
  if ($[10] !== item) {
    t7 = getCatalogPromotionPresentation(item);
    $[10] = item;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const promotion = t7;
  const src = item.imageUrl ? resolveImageUrl(item.imageUrl) : null;
  let t8;
  if ($[12] !== item.id || $[13] !== photoTransitions) {
    t8 = () => photoTransitions && isShopReturnTarget(item.id);
    $[12] = item.id;
    $[13] = photoTransitions;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const [isReturnTarget] = useState(t8);
  const t9 = interactive && opensDialog ? "dialog" : undefined;
  const t10 = item.id;
  const t11 = true;
  let t12;
  if ($[15] !== canPreload || $[16] !== preloadPagePhoto) {
    t12 = event => {
      if (!canPreload || event.pointerType !== "mouse") {
        return;
      }
      cancelHoverPreload();
      hoverTimer.current = window.setTimeout(preloadPagePhoto, HOVER_PRELOAD_DELAY_MS);
    };
    $[15] = canPreload;
    $[16] = preloadPagePhoto;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = interactive && "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-background";
  let t14;
  if ($[18] !== className || $[19] !== t13) {
    t14 = cx("group relative flex h-full w-full flex-col rounded-xl text-left", t13, className);
    $[18] = className;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const t15 = "relative aspect-square w-full overflow-hidden rounded-xl bg-background";
  const t16 = src ? <ShopPhotoTransition itemId={placement === "promotion" ? undefined : item.id}><Image src={src} alt={item.name} fill={true} sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" unoptimized={!canOptimizeImage(src)} loading={isReturnTarget ? "eager" : "lazy"} className="object-contain transition-transform duration-500 ease-out md:group-hover:scale-[1.03]" /></ShopPhotoTransition> : <div className="flex h-full w-full items-center justify-center text-foreground-muted"><FiImage aria-hidden={true} className="h-8 w-8" /><span className="sr-only">No photo</span></div>;
  let t17;
  if ($[21] !== item.isPopular) {
    t17 = item.isPopular && <PopularBadge tooltip={null} className="absolute left-2 top-2" />;
    $[21] = item.isPopular;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t16 || $[24] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[23] = t16;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = "mt-3 flex flex-col gap-1 border-b-2 border-border pb-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3";
  let t20;
  if ($[26] !== accentColor) {
    t20 = accentColor ? {
      borderColor: accentColor
    } : undefined;
    $[26] = accentColor;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== item.name) {
    t21 = <div className="flex min-h-[2.5rem] supports-[height:1lh]:min-h-[2lh] min-w-0 items-center text-body-strong"><h3 className="line-clamp-2 text-body-strong uppercase tracking-wide text-foreground transition-colors md:group-hover:text-primary" title={item.name}>{item.name}</h3></div>;
    $[28] = item.name;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  const t22 = "shrink-0 text-body-strong tabular-nums text-foreground";
  const t23 = promotion?.kind === "gift" ? <span className="text-caption text-foreground-secondary line-through">{formatCurrency(item.originalPrice ?? item.price)}</span> : <>{formatCurrency(currentPrice)}{savings > 0 && <span className="ml-1.5 text-caption text-foreground-secondary line-through">{formatCurrency(item.originalPrice ?? item.price)}</span>}</>;
  let t24;
  if ($[30] !== t23) {
    t24 = <p className={t22}>{t23}</p>;
    $[30] = t23;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] !== t20 || $[33] !== t21 || $[34] !== t24) {
    t25 = <div className={t19} style={t20}>{t21}{t24}</div>;
    $[32] = t20;
    $[33] = t21;
    $[34] = t24;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] !== promotion) {
    t26 = promotion && <p className="mt-2 line-clamp-2 text-caption text-foreground-secondary"><span className="font-semibold text-primary">{promotion.benefit}</span>{promotion.kind === "available" && <span>{` · ${promotion.action}`}</span>}{promotion.kind === "gift" && <span>{` ${promotion.action}`}</span>}</p>;
    $[36] = promotion;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== quantity) {
    t27 = quantity !== undefined && quantity > 0 && <p className="mt-2 flex items-center gap-1.5 text-caption text-foreground-secondary"><FiCheck aria-hidden={true} className="h-3.5 w-3.5 text-primary" /><span>{quantity} in your cart</span></p>;
    $[38] = quantity;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== Root || $[41] !== item.id || $[42] !== placement || $[43] !== preloadPagePhoto || $[44] !== rootProps || $[45] !== t12 || $[46] !== t14 || $[47] !== t18 || $[48] !== t25 || $[49] !== t26 || $[50] !== t27 || $[51] !== t9) {
    t28 = <Root {...rootProps} aria-haspopup={t9} data-shop-item-id={t10} data-shop-placement={placement} data-storefront-tile={t11} onPointerEnter={t12} onPointerLeave={cancelHoverPreload} onFocus={preloadPagePhoto} className={t14}>{t18}{t25}{t26}{t27}</Root>;
    $[40] = Root;
    $[41] = item.id;
    $[42] = placement;
    $[43] = preloadPagePhoto;
    $[44] = rootProps;
    $[45] = t12;
    $[46] = t14;
    $[47] = t18;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
    $[51] = t9;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  return t28;
};

/** Loading placeholder in the same shape as StorefrontProductTile. */
export const StorefrontProductTileSkeleton: React.FC = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="aspect-square w-full rounded-xl" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-full" aria-hidden={true}>{t0}<div className="mt-3 flex flex-col gap-1 border-b-2 border-border pb-3 text-body-strong sm:flex-row sm:items-center sm:justify-between sm:gap-3"><div className="flex min-h-[2.5rem] supports-[height:1lh]:min-h-[2lh] flex-1 items-center"><Skeleton className="h-4 w-2/3" /></div><Skeleton className="h-4 w-12" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};